import { ContentHeader } from "components/layout";
import { InlineBanner } from "components/ui/inline-banner";
import { GetWorkflowSettingsResult } from "generated/graphql";
import React from "react";
import { Link } from "react-router-dom";
import { entityTypeLabel, getWorkflowStatusIds } from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";
import { WorkflowActionSummary } from "./workflow-action-summary";
import { WorkflowDelaySummaryListItem } from "./workflow-delay-summary";
import { WorkflowSettingsField } from "./workflow-settings-field";
import {
    WorkflowClientStatusesSummary,
    WorkflowTriggerSummary,
} from "./workflow-trigger-summary";

interface UserWorkflowSettingsProps {
    workflow: EditableWorkflow;
    refetch: () => void;
    workflowSettings: GetWorkflowSettingsResult;
    canEditWorkflow: boolean;
}

export const UserWorkflowSettings: React.FC<UserWorkflowSettingsProps> = props => {
    const { workflow, refetch, workflowSettings, canEditWorkflow } = props;

    const settings = (workflow.settings || []).filter(s => s.scope === "user");

    const statusIds = getWorkflowStatusIds(workflow);

    return (
        <div>
            <div className="flex justify-between">
                <ContentHeader>{workflow.name}</ContentHeader>
            </div>
            <div className="space-y-4">
                <p className="text-sm text-gray-500">{workflow.description}</p>

                <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                        What this workflow does
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>
                                <span className="font-medium">Trigger:</span>{" "}
                                <WorkflowTriggerSummary workflow={workflow} />
                            </span>
                        </li>
                        {statusIds.length > 0 ? (
                            <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>
                                    <span className="font-medium">
                                        {entityTypeLabel(workflow.attachTo)} Statuses:
                                    </span>{" "}
                                    <WorkflowClientStatusesSummary workflow={workflow} />
                                </span>
                            </li>
                        ) : null}
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>
                                <span className="font-medium">Action:</span>{" "}
                                <WorkflowActionSummary
                                    workflow={workflow}
                                    workflowSettings={workflowSettings}
                                />
                            </span>
                        </li>
                        <WorkflowDelaySummaryListItem
                            workflow={workflow}
                            workflowSettings={workflowSettings}
                        />
                    </ul>
                    {canEditWorkflow ? (
                        <div className="mt-4">
                            <Link
                                to={`/settings/workflow/${workflow.id}`}
                                className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700">
                                Edit workflow
                                <svg
                                    className="ml-1 w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </Link>
                        </div>
                    ) : null}
                </div>

                {!canEditWorkflow && (
                    <InlineBanner type="info">
                        This workflow is managed by your administrator.
                    </InlineBanner>
                )}

                {settings.map(setting => (
                    <WorkflowSettingsField
                        key={`${workflow.id}.user.${setting.key}`}
                        workflow={workflow}
                        setting={setting}
                        workflowSettings={workflowSettings.user}
                        onSaved={refetch}
                    />
                ))}
            </div>
        </div>
    );
};
